/**
 * ============================================================================
 * SELECTABLE GIFTS — Custom Element
 * ============================================================================
 *
 * WICHTIG: Diese Klasse MUSS in einem eigenen Asset liegen und im Cart Drawer
 * UNBEDINGT geladen werden (nicht innerhalb von {% if cart != empty %}).
 *
 * Grund: Das <ds-selectable-gifts> Markup wird nur bei nicht-leerem Warenkorb
 * gerendert und steckt in einem [data-ajax-cart-section] Container. Liquid Ajax
 * Cart ersetzt diese Container per DOMParser + Node-Adoption. Inline <script>
 * Tags aus so eingefügtem HTML werden vom Browser NICHT ausgeführt. Lag die
 * customElements.define-Definition im Snippet, wurde sie bei leerem Warenkorb
 * beim Seitenload nie geladen und das Element nie upgraded (Gifts erschienen
 * erst nach Reload). Als separates, immer geladenes Asset ist die Definition
 * vorhanden, sobald das Element per Ajax injiziert wird.
 */

if (!customElements.get('ds-selectable-gifts')) {
    class DSSelectableGifts extends HTMLElement {
        constructor() {
            super();
            this.grid = this.querySelector('[data-gift-grid]');
            this.slotsLeftEl = this.querySelector('[data-slots-left]');
            this.template = this.querySelector('#ds-gift-item-template');
            this.addButton = this.querySelector('[data-add-gifts]');
            this.storageKey = 'active_campaign';
            this.selectedVariants = new Set();
            this.currentStepId = null;
            this.giftSelectionLimit = 0;

            this.onRequestStart = this.onRequestStart.bind(this);
            this.onRequestEnd = this.onRequestEnd.bind(this);
            this.onGiftClick = this.onGiftClick.bind(this);
            this.onAddClick = this.onAddClick.bind(this);
            this.onCampaignUpdated = this.onCampaignUpdated.bind(this);
        }

        connectedCallback() {
            document.addEventListener('liquid-ajax-cart:request-start', this.onRequestStart);
            document.addEventListener('liquid-ajax-cart:request-end', this.onRequestEnd);
            document.addEventListener('ds:campaign-updated', this.onCampaignUpdated);
            this.addButton.addEventListener('click', this.onAddClick);

            // This element lives inside a [data-ajax-cart-section], so Liquid Ajax
            // Cart re-creates it on every cart update and 'liquid-ajax-cart:init'
            // won't fire again. The element is connected DURING request-end-internal,
            // before LAC swaps in the updated cart state (R), so reading the cart
            // synchronously here would be stale (threshold not yet met -> stays
            // hidden). Defer to the next frame so window.liquidAjaxCart.cart is current.
            const renderWhenReady = () => {
                if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
                    this.cart = window.liquidAjaxCart.cart;
                    this.render();
                }
            };
            if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
                requestAnimationFrame(renderWhenReady);
            } else {
                document.addEventListener('liquid-ajax-cart:init', renderWhenReady, { once: true });
            }
        }

        disconnectedCallback() {
            document.removeEventListener('liquid-ajax-cart:request-start', this.onRequestStart);
            document.removeEventListener('liquid-ajax-cart:request-end', this.onRequestEnd);
            document.removeEventListener('ds:campaign-updated', this.onCampaignUpdated);
            this.addButton.removeEventListener('click', this.onAddClick);
        }

        // Kampagnenwechsel ohne Cart-Request (von ds-cart-ajax.js ausgelöst)
        onCampaignUpdated() {
            this.cart = window.liquidAjaxCart?.cart || this.cart;
            if (this.cart) this.render();
        }

        onGiftClick(event) {
            const item = event.currentTarget;
            const variantId = item.dataset.variantId;

            if (item.classList.contains('selected')) {
                // Deselect
                item.classList.remove('selected');
                this.selectedVariants.delete(variantId);
            } else if (this.giftSelectionLimit === 1) {
                // Single-select: clicking another swaps the selection
                this.grid.querySelectorAll('.ds-gift-item.selected').forEach((el) => {
                    el.classList.remove('selected');
                });
                this.selectedVariants.clear();
                item.classList.add('selected');
                this.selectedVariants.add(variantId);
            } else if (this.selectedVariants.size < this.giftSelectionLimit) {
                // Multi-select: add until limit reached
                item.classList.add('selected');
                this.selectedVariants.add(variantId);
            }

            this.updateAddButton();
        }

        updateAddButton() {
            // Button should only be enabled when EXACTLY the required amount is selected
            if (this.selectedVariants.size === this.giftSelectionLimit && this.giftSelectionLimit > 0) {
                this.addButton.disabled = false;
            } else {
                this.addButton.disabled = true;
            }
        }

        async onAddClick() {
            if (this.selectedVariants.size === 0) return;

            const items = Array.from(this.selectedVariants).map((variantId) => ({
                id: variantId,
                quantity: 1,
                properties: {
                    _custom_gift_step: String(this.currentStepId),
                },
            }));

            try {
                await window.liquidAjaxCart.add({ items });
                // Clear selections after successful add
                this.selectedVariants.clear();
                this.grid.querySelectorAll('.ds-gift-item').forEach((item) => {
                    item.classList.remove('selected');
                });
                this.updateAddButton();
            } catch (error) {
                console.error('Error adding gifts to cart:', error);
            }
        }

        onRequestStart() {
            this.classList.add('is-loading');
            this.querySelectorAll('button').forEach((btn) => (btn.disabled = true));
        }

        onRequestEnd(event) {
            this.classList.remove('is-loading');
            this.querySelectorAll('button').forEach((btn) => (btn.disabled = false));

            // Always re-render: the campaign is written to localStorage by
            // ds-cart-ajax.js's campaignLoaderMutation DURING the request, so the
            // first init() may have found no campaign. Falling back to the global
            // cart (event.detail.cart isn't always present) lets us self-heal and
            // show the gifts without a page reload.
            this.cart = event.detail?.cart || window.liquidAjaxCart?.cart;
            if (this.cart) {
                this.render();
            }
        }

        getCampaign() {
            try {
                const stored = localStorage.getItem(this.storageKey);
                return stored ? JSON.parse(stored) : null;
            } catch (e) {
                console.error('Error reading campaign from storage', e);
                return null;
            }
        }

        isStepEligible(step) {
            // Check if step has required products
            if (!step.required_products || step.required_products.length === 0) {
                return true; // No requirements, step is eligible
            }

            // Check if at least one required product meets the quantity requirement
            for (let i = 0; i < step.required_products.length; i++) {
                const requiredProduct = step.required_products[i];
                const requiredQuantity =
                    step.required_quantity && step.required_quantity[i]
                        ? parseInt(step.required_quantity[i], 10)
                        : 1;

                // Sum quantities of this product in cart (excluding campaign gifts)
                const totalQuantity = this.cart.items.reduce((sum, item) => {
                    // Check if item matches the required product and is NOT a campaign gift.
                    // Shopify's cart API always returns `properties` as an object (`{}` when
                    // none are set, not `null`), and `{}` is truthy — so a plain `!item.properties`
                    // check excludes almost every real cart item, not just tagged gifts.
                    const hasNoProperties = Object.keys(item.properties || {}).length === 0;
                    if (item.product_id === parseInt(requiredProduct.id, 10) && hasNoProperties) {
                        return sum + item.quantity;
                    }
                    return sum;
                }, 0);

                // If this required product meets the quantity requirement, step is eligible
                if (totalQuantity >= requiredQuantity) {
                    return true;
                }
            }

            // None of the required products met the quantity requirement
            return false;
        }

        render() {
            const campaign = this.getCampaign();
            if (!campaign) {
                this.hidden = true;
                return;
            }

            const cartTotal = window.dsGetGiftThresholdValue
                ? window.dsGetGiftThresholdValue(this.cart)
                : this.cart.total_price / 100;

            const eligibleSteps = campaign.steps.filter(
                (step) =>
                    step.type === 'selectable_gifts' &&
                    cartTotal >= step.free_from_amount &&
                    this.isStepEligible(step)
            );

            if (eligibleSteps.length === 0) {
                this.hidden = true;
                return;
            }

            this.grid.innerHTML = '';
            let totalSlotsLeft = 0;
            let hasRenderedGifts = false;

            // Reset for re-render
            this.currentStepId = null;
            this.giftSelectionLimit = 0;

            eligibleSteps.forEach((step) => {
                const limit = step.gift_selection_limit || 1;
                const stepId = step.id;

                // Store the first eligible step's info
                if (!this.currentStepId) {
                    this.currentStepId = stepId;
                }

                const giftVariantIds = step.gift_products.map((p) => p.variant_id);
                // Nur bereits als Geschenk HINZUGEFÜGTE Lines dieses Steps zählen
                // (variant_id + _custom_gift_step). Sonst würde ein vom Kunden
                // regulär gekaufter Artikel mit derselben Variante fälschlich als
                // belegter Geschenk-Slot gezählt und die Auswahl verschwindet/schrumpft
                // grundlos.
                const currentCount = this.cart.items.reduce((sum, item) => {
                    if (
                        giftVariantIds.includes(item.variant_id) &&
                        item.properties?._custom_gift_step === String(stepId)
                    ) {
                        return sum + item.quantity;
                    }
                    return sum;
                }, 0);

                const slotsLeft = Math.max(0, limit - currentCount);
                totalSlotsLeft += slotsLeft;

                if (slotsLeft > 0) {
                    step.gift_products.forEach((product) => {
                        // WICHTIG: Property heißt _custom_gift_step (siehe onAddClick),
                        // nicht _step_id — mit dem falschen Namen war dieser Check
                        // immer false und bereits hinzugefügte Geschenke blieben im
                        // Auswahl-Grid sichtbar/erneut wählbar.
                        const isInCart = this.cart.items.some(
                            (item) =>
                                item.variant_id === product.variant_id &&
                                item.properties?._custom_gift_step === String(stepId)
                        );

                        if (!isInCart) {
                            this.renderGift(product, stepId);
                            hasRenderedGifts = true;
                        }
                    });
                }
            });

            if (hasRenderedGifts) {
                this.hidden = false;
                this.slotsLeftEl.textContent = totalSlotsLeft;
                // Store the actual remaining slots as the selection limit
                this.giftSelectionLimit = totalSlotsLeft;

                // render() re-runs on every cart request-end, document-wide (see
                // onRequestEnd), not just ones caused by this widget. Re-selecting
                // gift 1 unconditionally here would silently discard a customer's
                // manual choice whenever an unrelated cart update (e.g. toggling a
                // bundle) happens to fire in the background. Carry forward any
                // previous selection that's still present in the freshly rendered
                // grid, and only fall back to the gift-1 default when nothing
                // carries over (i.e. this is truly the first render).
                const previousSelection = this.selectedVariants;
                this.selectedVariants = new Set();
                this.grid.querySelectorAll('.ds-gift-item').forEach((item) => {
                    if (
                        previousSelection.has(item.dataset.variantId) &&
                        this.selectedVariants.size < this.giftSelectionLimit
                    ) {
                        item.classList.add('selected');
                        this.selectedVariants.add(item.dataset.variantId);
                    }
                });

                if (this.selectedVariants.size === 0) {
                    const firstGift = this.grid.querySelector('.ds-gift-item');
                    if (firstGift && this.giftSelectionLimit > 0) {
                        firstGift.classList.add('selected');
                        this.selectedVariants.add(firstGift.dataset.variantId);
                    }
                }
                this.updateAddButton();
            } else {
                this.hidden = true;
            }
        }

        renderGift(product, stepId) {
            const clone = this.template.content.cloneNode(true);
            const item = clone.querySelector('.ds-gift-item');

            const img = item.querySelector('.ds-gift-item__image');
            img.src = product.featured_image;
            img.alt = product.title;

            const variantTitle = product.variant_title && product.variant_title !== 'Default Title'
                ? product.variant_title
                : '';
            clone.querySelector('.ds-gift-item__title').textContent = variantTitle
                ? `${product.title} - ${variantTitle}`
                : product.title;

            // Set the variant ID as data attribute
            item.dataset.variantId = product.variant_id;

            // Add click handler
            item.addEventListener('click', this.onGiftClick);

            this.grid.appendChild(clone);
        }
    }

    customElements.define('ds-selectable-gifts', DSSelectableGifts);
}